import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { SubmitButton } from "@/components/ui/button";
import {
  SubdistrictPayloadSchema,
  subdistrictPayloadSchema,
  useSubdistrict,
  useSubdistrictEdit,
} from "@/api/subdistrict";
import { Loading } from "@/components/ui/loading";
import { SubdistrictFormFields } from "./SubdistrictFormFields";
import { FormContainer } from "@/components/form/form-container";
import { Anchor } from "@/components/ui/anchor";
import { ErrorMessageFromResponse } from "@/api/types";

export function SubdistrictEditForm({ id }: { id: string }) {
  const { data: subdistrict, isPending: isLoadingData } = useSubdistrict({
    id,
  });
  const form = useForm<SubdistrictPayloadSchema>({
    resolver: zodResolver(subdistrictPayloadSchema),
    defaultValues: {
      subdistrictCode: subdistrict?.subdistrictCode,
      subdistrictName: subdistrict?.subdistrictName,
      city: subdistrict?.city,
    },
    values: subdistrict,
  });
  const { mutate, isPending: isSubmitting } = useSubdistrictEdit();
  const navigate = useNavigate();

  const handleSubmit = (values: SubdistrictPayloadSchema) => {
    mutate(
      { id: id, data: values },
      {
        onSuccess: () => {
          swalSuccess
            .fire({
              text: "District has been updated",
            })
            .then(() => navigate("/master-data/district"));
        },
        onError: (error) => {
          swalError.fire({
            text:
              (error as ErrorMessageFromResponse).response?.data.data ??
              error.message,
          });
        },
      },
    );
  };

  if (isLoadingData) {
    return <Loading />;
  }

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <SubdistrictFormFields />
      <div className="ml-auto flex gap-x-2">
        <Anchor href="/master-data/district" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton loading={isSubmitting} />
      </div>
    </FormContainer>
  );
}
